/**
 * S49.1 / S49.2 — continuous session-expiry detection.
 *
 * ⚠️ WHAT WAS WRONG, because it explains every design choice below.
 *
 * Users reported that an expired session left them sitting on the page, able to click around,
 * until they refreshed and were correctly sent to /login. That had THREE independent causes,
 * each sufficient on its own:
 *
 *   1. `useAuthInit()` and `setupAuthEventListeners()` in useAuth.ts decoded the JWT and
 *      compared `exp` -- and were NEVER CALLED ANYWHERE. The only client-side expiry check in
 *      the product was dead code.
 *   2. Nothing re-evaluated expiry as time passed: no interval, no visibilitychange handler.
 *      Once a page had rendered, the app had no mechanism by which it COULD notice.
 *   3. The reactive `auth:unauthorized` path was wired at 15 of the 68 files that send an
 *      Authorization header, across 322 raw fetch() sites. Most 401s never signalled expiry.
 *
 * A refresh always worked because ProtectedRoute's MOUNT effect calls
 * /me/effective-permissions and handles its 401. The only reliable detector in the product ran
 * at mount -- which is exactly the asymmetry users noticed. This hook makes detection
 * continuous. S49.3 makes the 401 path structural.
 *
 * ⚠️ THIS IS A UX MECHANISM, NOT AN AUTHORISATION CONTROL. A client's clock can be wrong or
 * deliberately set back, so `exp` checked here can never be trusted to deny access -- the
 * server remains the only authority. What this buys is that a user is told their session ended
 * instead of discovering it by losing work.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { setAuthToken } from '../api-client';
import { rememberReturnPath } from '../lib/returnPath';
import { refreshAccessToken, storeRefreshToken } from '../lib/tokenRefresh';
import { useAuthStore } from '../stores/authStore';

/** How often to re-evaluate. 15s bounds the "dead page" window without being a busy loop. */
const CHECK_INTERVAL_MS = 15_000;

/** How long before expiry to warn. Enough to finish a sentence, not so long it is background noise. */
export const WARNING_THRESHOLD_SECONDS = 120;

/**
 * Extract `exp` (in milliseconds) from a JWT, or null if it has none or cannot be read.
 *
 * ⚠️ Returns null rather than throwing, and null means "cannot determine expiry" -- which
 * callers must treat as "do not expire them", NOT as "expired". A parse failure logging
 * everyone out would turn one malformed token into an outage.
 */
export function parseTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    const payload = JSON.parse(
      atob(base64.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    const exp = payload.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export interface SessionExpiryState {
  /** Epoch ms at which the token expires, or null if unknown. */
  expiresAt: number | null;
  /** Whole seconds remaining; null when expiry is unknown. Never negative. */
  secondsRemaining: number | null;
  /** Within WARNING_THRESHOLD_SECONDS of expiry and not dismissed. */
  isWarning: boolean;
  /** Hide the warning for the remainder of this session. */
  dismissWarning: () => void;
  /** End the session now and go to /login. Exposed so the warning can offer it. */
  endSession: () => void;
  /** S49.6: renew in place. Resolves true when the session was extended. */
  extendSession: () => Promise<boolean>;
}

/**
 * Watch the access token and end the session the moment it expires.
 *
 * Call once, inside the authenticated tree.
 */
export function useSessionExpiry(): SessionExpiryState {
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const expiresAt = parseTokenExpiry(token);

  // ⚠️ Guards against a double logout: the interval and the visibilitychange handler can
  // observe the same expiry within a tick of each other, and logout() + navigate() twice
  // produces a double history entry that breaks the browser Back button on /login.
  const endedRef = useRef(false);

  // A new token (login, session switch) re-arms everything.
  useEffect(() => {
    endedRef.current = false;
    setDismissed(false);
  }, [token]);

  const endSession = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    // S49.4: stash where they were so re-authentication returns them there, not to the
    // dashboard. Route only -- never form contents; see lib/returnPath.ts.
    rememberReturnPath(window.location.pathname + window.location.search);
    storeRefreshToken(null); // S49.6: a dead session must not leave a live refresh token
    setAuthToken(null);
    logout();
    navigate('/login', { replace: true, state: { reason: 'expired' } });
  }, [logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    // ⚠️ Sync the persisted token into sessionStorage, which is where the api-client middleware
    // reads it (api-client/index.ts onRequest). This was previously done ONLY by the dead
    // useAuthInit(), and the omission has a visible symptom: zustand persists the token to
    // localStorage (shared across tabs) while sessionStorage is per-tab, so opening the app in
    // a NEW TAB left every apiClient call unauthenticated while raw fetch() calls reading the
    // store still worked. Inconsistent by construction, and it presented as a random logout.
    setAuthToken(token);

    // No expiry claim: nothing to count down. Leave the session alone and let the server decide.
    if (expiresAt === null) {
      setSecondsRemaining(null);
      return;
    }

    const evaluate = () => {
      // ⚠️ Compare TIMESTAMPS, never count down with setTimeout. Timers do not fire while a
      // laptop is asleep, so a countdown scheduled for "in 15 minutes" fires long after the
      // token actually died -- reproducing the very bug this hook exists to fix, in a form
      // that only appears on resume and is therefore much harder to catch.
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setSecondsRemaining(remaining);
      if (remaining <= 0) endSession();
    };

    evaluate(); // Immediately, so a token already dead on mount never renders a usable page.

    const interval = window.setInterval(evaluate, CHECK_INTERVAL_MS);
    // Catches the resume-from-sleep and switch-back-to-tab cases far sooner than the interval.
    window.addEventListener('visibilitychange', evaluate);
    window.addEventListener('focus', evaluate);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('visibilitychange', evaluate);
      window.removeEventListener('focus', evaluate);
    };
  }, [isAuthenticated, token, expiresAt, endSession]);

  const dismissWarning = useCallback(() => setDismissed(true), []);

  /**
   * S49.6: renew the session in place.
   *
   * ⚠️ Deliberately does NOT end the session when renewal fails. A failed renewal may be a
   * flat network -- signing someone out because their wifi blipped would be a worse bug than
   * the one this sprint set out to fix. The expiry timer above is the single authority on
   * when a session is over; this only ever extends it.
   */
  const extendSession = useCallback(async (): Promise<boolean> => {
    const renewed = await refreshAccessToken();
    if (renewed) setDismissed(false);
    return renewed !== null;
  }, []);

  // Attempt a silent renewal as soon as the warning window opens, so most users never see the
  // modal at all. `refreshAccessToken` is single-flight, so the interval re-entering this
  // while a renewal is in progress joins the existing request rather than starting a second
  // one -- which matters, because a second one would present an already-rotated token and the
  // server would correctly treat that as theft and revoke the whole session family.
  const withinWarning =
    secondsRemaining !== null && secondsRemaining > 0 && secondsRemaining <= WARNING_THRESHOLD_SECONDS;

  useEffect(() => {
    if (!withinWarning || !isAuthenticated) return;
    void refreshAccessToken();
  }, [withinWarning, isAuthenticated, secondsRemaining]);

  return {
    expiresAt,
    secondsRemaining,
    isWarning:
      !dismissed &&
      secondsRemaining !== null &&
      secondsRemaining > 0 &&
      secondsRemaining <= WARNING_THRESHOLD_SECONDS,
    dismissWarning,
    endSession,
    extendSession,
  };
}
